/* ------------------------------------------------------------------ *
 *  Front Desk – Contacts, Scheduled events and Call Logs API client.
 *  Same gateway and same sign-in as dashboardApi.ts (ApiOptions is shared).
 * ------------------------------------------------------------------ */
import { DEFAULT_API_BASE } from "./dashboardApi";
import type { ApiOptions } from "./dashboardApi";

/* ---------- types (match the Calls service DTOs) ---------- */

export type ContactTag = "Friends" | "Work" | "Family" | "Important";
export type ContactSource = "Device" | "Gmail" | "iCloud" | "Outlook";

export interface ChannelDto {
  label: string;
  value: string;
}

export interface ContactDto {
  id: string;
  name: string;
  company: string | null;
  role: string | null;
  tags: string[];
  favorite: boolean;
  source: string;
  shared: string | null;
  phones: ChannelDto[];
  emails: ChannelDto[];
  /** yyyy-MM-dd */
  birthday: string | null;
  facebook: string | null;
  x: string | null;
  website: string | null;
}

export interface CreateContactBody {
  name: string;
  company?: string | undefined;
  role?: string | undefined;
  tags?: string[] | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  source?: string | undefined;
  shared?: string | undefined;
}

export interface ScheduledEventDto {
  id: string;
  title: string;
  description: string | null;
  /** yyyy-MM-dd (the day picked in the organisation's time zone) */
  date: string;
  /** HH:mm, 24 hours */
  time: string;
  isPublic: boolean;
  extra: number;
  people: string[];
}

export interface CreateEventBody {
  title: string;
  description?: string | undefined;
  date: string;
  time: string;
  people: string[];
}

export type LogType = "incoming" | "outgoing" | "missed" | "rejected" | "ongoing";
export type LogTab = "all" | "missed" | "ongoing" | "recorded";
export type LogSort = "date" | "name" | "duration";

export interface CallLogRowDto {
  id: string;
  name: string | null;
  number: string;
  type: LogType;
  seconds: number;
  /** ISO, UTC */
  startedAt: string;
  agentId: string | null;
  agent: string | null;
  queueId: string | null;
  inbox: string | null;
  recorded: boolean;
  recordingId: string | null;
}

export interface NamedId {
  id: string;
  name: string;
}

export interface CallLogResult {
  items: CallLogRowDto[];
  total: number;
  page: number;
  pageSize: number;
  counts: { all: number; missed: number; ongoing: number; recorded: number };
  agents: NamedId[];
  queues: NamedId[];
  serverTime: string;
}

export interface CallLogQuery {
  tab?: LogTab | undefined;
  type?: LogType | undefined;
  queueId?: string | undefined;
  agentId?: string | undefined;
  search?: string | undefined;
  sort?: LogSort | undefined;
  desc?: boolean | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

/** What the signed-in front desk user dials with. Give it to <Frontdesk dial={...} />; every field is optional. */
export interface DialDefaults {
  /** Caller ID shown to the customer: one of the organisation's numbers (E.164). */
  fromNumber?: string | undefined;
  fromNumberId?: string | undefined;
  /** Who takes the call on the agent side. */
  agentId?: string | undefined;
  agentSipUsername?: string | undefined;
  agentNumber?: string | undefined;
  /** Who listens in on a live call. */
  supervisorSipUsername?: string | undefined;
  supervisorNumber?: string | undefined;
}

export interface DialRequestBody {
  to: string;
  queueId?: string | undefined;
}

/* ---------- plumbing ---------- */

const TOKEN_KEYS = ["access_token", "accessToken", "token", "jwt"];

const readStoredToken = (): string | null => {
  try {
    for (const key of TOKEN_KEYS) {
      const v = window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
      if (v) return v.replace(/^"|"$/g, "");
    }
  } catch {
    /* storage blocked */
  }
  return null;
};

const baseOf = (opts: ApiOptions) => (opts.apiBase ?? DEFAULT_API_BASE).replace(/\/+$/, "");

const authHeaders = async (opts: ApiOptions): Promise<Record<string, string>> => {
  const token = (await opts.getToken?.()) ?? readStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const failure = async (res: Response, what: string): Promise<Error> => {
  let detail = "";
  try {
    const body = await res.json();
    detail = body?.message ?? body?.detail ?? body?.title ?? "";
  } catch {
    /* not json */
  }
  if (res.status === 401) return new Error("Please sign in again.");
  if (res.status === 403) return new Error(`You do not have permission to ${what}.`);
  return new Error(detail || `Request failed (${res.status})`);
};

async function request<T>(
  opts: ApiOptions,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  what: string,
  init?: { params?: Record<string, string | number | boolean | undefined> | undefined; body?: unknown; signal?: AbortSignal | undefined },
): Promise<T> {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(init?.params ?? {})) if (v !== undefined && v !== "") query.set(k, String(v));
  const hasBody = init?.body !== undefined;

  const res = await fetch(`${baseOf(opts)}${path}${query.toString() ? `?${query}` : ""}`, {
    method,
    ...(init?.signal ? { signal: init.signal } : {}),
    headers: { Accept: "application/json", ...(hasBody ? { "Content-Type": "application/json" } : {}), ...(await authHeaders(opts)) },
    ...(hasBody ? { body: JSON.stringify(init?.body) } : {}),
  });
  if (!res.ok) throw await failure(res, what);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/* ---------- contacts ---------- */

/** Loads the whole address book (pages of 500). */
export async function listContacts(opts: ApiOptions, signal?: AbortSignal): Promise<ContactDto[]> {
  const all: ContactDto[] = [];
  for (let page = 1; page <= 20; page++) {
    const r = await request<{ items: ContactDto[]; total: number }>(opts, "GET", "/api/contacts", "see contacts", {
      params: { page, pageSize: 500, sort: "name", desc: false },
      signal,
    });
    all.push(...(r.items ?? []));
    if (all.length >= (r.total ?? 0) || (r.items ?? []).length === 0) break;
  }
  return all;
}

export const createContact = (opts: ApiOptions, body: CreateContactBody) =>
  request<ContactDto>(opts, "POST", "/api/contacts", "add contacts", { body });

export const setContactFavorite = (opts: ApiOptions, id: string, favorite: boolean) =>
  request<ContactDto>(opts, "PUT", `/api/contacts/${id}/favorite`, "change contacts", { body: { favorite } });

export const deleteContact = (opts: ApiOptions, id: string) =>
  request<void>(opts, "DELETE", `/api/contacts/${id}`, "delete contacts");

/* ---------- scheduled events ---------- */

export const listEvents = (opts: ApiOptions, signal?: AbortSignal) =>
  request<ScheduledEventDto[]>(opts, "GET", "/api/scheduled-events", "see scheduled events", { signal });

export const createEvent = (opts: ApiOptions, body: CreateEventBody) =>
  request<ScheduledEventDto>(opts, "POST", "/api/scheduled-events", "schedule events", { body });

export const repeatEvent = (opts: ApiOptions, id: string) =>
  request<ScheduledEventDto>(opts, "POST", `/api/scheduled-events/${id}/repeat`, "schedule events");

export const deleteEvent = (opts: ApiOptions, id: string) =>
  request<void>(opts, "DELETE", `/api/scheduled-events/${id}`, "delete events");

/* ---------- call logs ---------- */

export const getCallLog = (opts: ApiOptions, q: CallLogQuery, signal?: AbortSignal) =>
  request<CallLogResult>(opts, "GET", "/api/calls/log", "see the call log", {
    params: { ...q, search: q.search?.trim() || undefined },
    signal,
  });

export const hideCallLog = (opts: ApiOptions, id: string) =>
  request<void>(opts, "DELETE", `/api/calls/${id}/log`, "delete call logs");

/** Click-to-dial: rings the agent first, then the customer. Returns the new call. */
export const dialNumber = (opts: ApiOptions, dial: DialDefaults | undefined, body: DialRequestBody) =>
  request<{ id: string }>(opts, "POST", "/api/calls/dial", "make calls", {
    body: {
      to: body.to,
      queueId: body.queueId,
      fromNumber: dial?.fromNumber,
      fromNumberId: dial?.fromNumberId,
      agentId: dial?.agentId,
      agentSipUsername: dial?.agentSipUsername,
      agentNumber: dial?.agentNumber,
    },
  });

/** End a live call (the front desk user needs the permission to control calls). */
export const hangupCall = (opts: ApiOptions, callId: string) =>
  request<void>(opts, "POST", `/api/calls/${callId}/hangup`, "end calls");

/** Listen in on a live call (monitor: the agent and the customer cannot hear you). */
export const listenIn = (opts: ApiOptions, dial: DialDefaults | undefined, callId: string) =>
  request<unknown>(opts, "POST", `/api/calls/${callId}/supervise`, "listen in", {
    body: { role: "monitor", supervisorSipUsername: dial?.supervisorSipUsername, supervisorNumber: dial?.supervisorNumber },
  });

/** True when the front desk knows who to ring and which number to show, so click-to-dial can work. */
export const canDial = (dial: DialDefaults | undefined) =>
  !!dial && !!(dial.fromNumber || dial.fromNumberId) && !!(dial.agentId || dial.agentSipUsername || dial.agentNumber);

export const canListen = (dial: DialDefaults | undefined) => !!dial && !!(dial.supervisorSipUsername || dial.supervisorNumber);

/** Authenticated URL of a recording's audio. Resolves to a blob URL the caller must revoke. */
export async function fetchRecordingAudio(opts: ApiOptions, recordingId: string): Promise<string> {
  const res = await fetch(`${baseOf(opts)}/api/recordings/${recordingId}/audio`, { headers: await authHeaders(opts) });
  if (!res.ok) throw await failure(res, "play recordings");
  return URL.createObjectURL(await res.blob());
}

/* ---------- generic access for the module pages (any endpoint of the catalog) ---------- */

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export const apiCall = <T = unknown>(
  opts: ApiOptions,
  method: HttpMethod,
  path: string,
  init?: { params?: Record<string, string | number | boolean | undefined> | undefined; body?: unknown; signal?: AbortSignal | undefined },
) => request<T>(opts, method, path, method === "GET" ? "open this page" : "do this", init);

/** Downloads a file endpoint (CSV export, audio, logo ...) through the signed-in session and hands it to the browser. */
export async function apiDownload(
  opts: ApiOptions,
  method: HttpMethod,
  path: string,
  fallbackName: string,
  params?: Record<string, string | number | boolean | undefined>,
  body?: unknown,
): Promise<void> {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) if (v !== undefined && v !== "") query.set(k, String(v));
  const res = await fetch(`${baseOf(opts)}${path}${query.toString() ? `?${query}` : ""}`, {
    method,
    headers: { ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(await authHeaders(opts)) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) throw await failure(res, "download this file");
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = named ? decodeURIComponent(named) : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Uploads one file (multipart "file") to an endpoint such as a CSV import. */
export async function apiUpload<T = unknown>(opts: ApiOptions, path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${baseOf(opts)}${path}`, { method: "POST", headers: await authHeaders(opts), body: form });
  if (!res.ok) throw await failure(res, "upload files");
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}